import * as React from 'react';

/**
 * Entry-only motion for the chart primitives (T-0241).
 *
 * The prototype animates a screen when it is entered (a tab, a row) and
 * never on an ordinary re-render; under `prefers-reduced-motion` nothing
 * moves. `useEntryMotion(motionKey)` gives a chart:
 *
 *   - `animate`: true for the first moments after the chart mounts or its
 *     `motionKey` changes, then false, so marks that appear later (a
 *     refetch adding a point) do not animate. Always false under reduced
 *     motion. The chart puts `.chart-anim` on its root while it is true.
 *   - `runKey`: bumps when `motionKey` changes; the chart keys its root on
 *     it so the CSS animations start again from the first frame.
 *   - `previousKey`: the key before the last change (the gauge animates
 *     from the old value to the new one).
 */

/** Longest entry animation (stagger included) before the class comes off. */
export const ENTRY_MS = 2600;

const QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(onChange: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia(QUERY);
  mql.addEventListener?.('change', onChange);
  return () => mql.removeEventListener?.('change', onChange);
}

/** No `matchMedia` (server, tests without a stub) reads as reduced: nothing moves. */
function readReduced(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia(QUERY).matches;
}

export function usePrefersReducedMotion(): boolean {
  return React.useSyncExternalStore(subscribe, readReduced, () => true);
}

interface MotionState<K> {
  key: K;
  previousKey: K | undefined;
  run: number;
  animate: boolean;
}

export function useEntryMotion<K>(motionKey: K): {
  animate: boolean;
  runKey: number;
  previousKey: K | undefined;
} {
  const reduced = usePrefersReducedMotion();
  const [state, setState] = React.useState<MotionState<K>>({
    key: motionKey,
    previousKey: undefined,
    run: 0,
    animate: true,
  });

  // A new key is derived during render, so the frame that shows the new
  // data already carries the animation class (no flash of the end state).
  let view = state;
  if (!Object.is(state.key, motionKey)) {
    view = { key: motionKey, previousKey: state.key, run: state.run + 1, animate: true };
    setState(view);
  }

  React.useEffect(() => {
    if (!view.animate) return;
    const t = setTimeout(() => setState((s) => ({ ...s, animate: false })), ENTRY_MS);
    return () => clearTimeout(t);
  }, [view.animate, view.run]);

  return {
    animate: view.animate && !reduced,
    runKey: view.run,
    previousKey: view.previousKey,
  };
}

/**
 * A number that counts from `from` to `to` over a second (ease-out cubic),
 * as the prototype's `data-count` figures do. Shows `to` at once when
 * `active` is false (reduced motion, or no change).
 */
export function useCountUp(to: number | null, from: number, active: boolean, runKey: unknown) {
  const end = to === null ? null : Math.round(to);
  const [shown, setShown] = React.useState<number | null>(active ? Math.round(from) : end);

  React.useEffect(() => {
    if (to === null || !active || from === to || typeof requestAnimationFrame === 'undefined') {
      setShown(end);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / 1000);
      const e = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(from + (to - from) * e));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    setShown(from);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // `from` and `active` are read when `to` or the run changes, not tracked:
    // runKey restarts the count when the chart re-enters.
  }, [to, runKey]);

  return shown;
}
